/**
 * Luca Agent chat service – single API base for conversations and send.
 * REACT_APP_API_URL = Chat API base (e.g. https://woixrcq6lg.execute-api.us-east-1.amazonaws.com).
 */

const UNAVAILABLE_MESSAGE = 'Chat is not connected to a backend.';

const CHAT_API_BASE = 'https://woixrcq6lg.execute-api.us-east-1.amazonaws.com';

function generateId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function initSession() {
  return generateId();
}

function getOrCreateSessionId() {
  return initSession();
}

function getApiUrl() {
  // CRA: REACT_APP_API_URL. Vite: VITE_CHAT_API_BASE_URL (backend may reference this).
  const url = (
    process.env.REACT_APP_API_URL ||
    process.env.VITE_CHAT_API_BASE_URL ||
    CHAT_API_BASE
  ).replace(/\/$/, '');
  return url;
}

function getConversationsApiUrl() {
  return getApiUrl();
}

function getChatStreamUrl() {
  return getApiUrl();
}

function persistSessionId() {}

/**
 * Parse SSE stream and accumulate content; optional onChunk for live updates.
 * Resolves with { text, raw: { sessionId } }.
 */
function sendMessage(options, streamOptions) {
  const { message, sessionId, userId, idToken } = options || {};
  const onChunk = (streamOptions && streamOptions.onChunk) || null;

  const baseUrl = getApiUrl();
  if (!baseUrl) {
    return Promise.resolve({
      text: UNAVAILABLE_MESSAGE,
      raw: {},
    });
  }
  const url = baseUrl + '/api/chat/send';
  const body = JSON.stringify({
    message: message || '',
    conversation_id: sessionId || null,
  });

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (idToken) {
    headers.Authorization = 'Bearer ' + idToken;
  }

  return fetch(url, {
    method: 'POST',
    headers,
    body,
  })
    .then(function (response) {
      const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
      if (!response.ok) {
        return response.text().then(function (bodyText) {
          let detail = bodyText;
          try {
            const j = JSON.parse(bodyText);
            detail = j.detail || j.message || detail;
          } catch (_) {}
          const err = new Error(detail || response.statusText);
          err.status = response.status;
          err.detail = detail;
          throw err;
        });
      }

      if (contentType.indexOf('text/event-stream') !== -1 && response.body) {
        return readSSEStream(response, onChunk);
      }

      return response.json().then(function (data) {
        const text =
          (data && (data.reply != null ? data.reply : data.text != null ? data.text : data.content)) || '';
        const newSessionId =
          (data && (data.sessionId != null ? data.sessionId : data.conversation_id)) || sessionId;
        return {
          text,
          raw: { sessionId: newSessionId },
        };
      });
    })
    .catch(function (err) {
      if (err.status !== undefined) throw err;
      const wrap = new Error(err.message || UNAVAILABLE_MESSAGE);
      wrap.status = 0;
      wrap.detail = err.message;
      throw wrap;
    });
}

function readSSEStream(response, onChunk) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let conversationId = null;

  function processLine(line) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) return;
    const raw = trimmed.slice(5).trim();
    if (raw === '' || raw === '[DONE]') return;
    try {
      const data = JSON.parse(raw);
      if (data.type === 'chunk' && data.text != null) {
        const chunk = typeof data.text === 'string' ? data.text : String(data.text);
        fullText += chunk;
        if (typeof onChunk === 'function') onChunk(chunk);
      }
      if (data.type === 'done' && data.conversation_id != null) {
        conversationId = data.conversation_id;
      }
    } catch (_) {}
  }

  function read() {
    return reader.read().then(function (result) {
      if (result.done) {
        if (buffer.length) processLine(buffer);
        return { text: fullText, raw: { sessionId: conversationId } };
      }
      buffer += decoder.decode(result.value, { stream: true });
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() || '';
      lines.forEach(processLine);
      return read();
    });
  }

  return read();
}

/**
 * List conversations. Returns array of { id, title, updated_at }.
 */
function getConversations(token) {
  const baseUrl = getConversationsApiUrl();
  if (!baseUrl) return Promise.resolve([]);

  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = 'Bearer ' + token;

  return fetch(baseUrl + '/api/chat/conversations', { method: 'GET', headers })
    .then(function (response) {
      if (!response.ok) {
        return response.text().then(function (t) {
          const err = new Error(t || response.statusText);
          err.status = response.status;
          throw err;
        });
      }
      return response.json();
    })
    .then(function (data) {
      const list = (data && data.conversations) || (Array.isArray(data) ? data : []);
      return list;
    })
    .catch(function () {
      return [];
    });
}

/**
 * Get one conversation. Returns { id, title, messages, updated_at }.
 */
function getConversation(id, token) {
  const baseUrl = getConversationsApiUrl();
  if (!baseUrl) return Promise.resolve({ messages: [], title: 'Chat' });

  const headers = { Accept: 'application/json' };
  if (token) headers.Authorization = 'Bearer ' + token;

  return fetch(baseUrl + '/api/chat/conversations/' + encodeURIComponent(id), { method: 'GET', headers })
    .then(function (response) {
      if (!response.ok) {
        return response.text().then(function (t) {
          const err = new Error(t || response.statusText);
          err.status = response.status;
          throw err;
        });
      }
      return response.json();
    })
    .then(function (data) {
      return {
        id: (data && data.id) || id,
        title: (data && data.title) || 'Chat',
        messages: (data && data.messages) || [],
        updated_at: data && data.updated_at,
      };
    })
    .catch(function (err) {
      if (err.status === 404) return { id, title: 'Chat', messages: [] };
      throw err;
    });
}

export {
  getApiUrl,
  getConversationsApiUrl,
  getChatStreamUrl,
  initSession,
  getOrCreateSessionId,
  persistSessionId,
  sendMessage,
  getConversations,
  getConversation,
  UNAVAILABLE_MESSAGE,
};
